import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { api_url } from "../../constant/constant";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import "./EmailTemplates.css";
import { showToast } from "../../Components/ShowToast/ToastNotification";
import { Modal, Button } from "react-bootstrap";

const EmailTemplates = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [data, setUsers] = useState([]);
  const [allData, setAllData] = useState([]);
  const [links, setLinks] = useState([]);
  const [searchVal, setSearchVal] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const abortRef = useRef(null);

  const sanitizeLabel = (htmlString) => {
    const temp = document.createElement("div");
    temp.innerHTML = htmlString || "";
    return temp.textContent || temp.innerText || "";
  };

  const extractPage = (url) => {
    if (!url) return 1;

    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.searchParams.get("page") || 1;
    } catch {
      const match = url.match(/[?&]page=(\d+)/);
      return match ? match[1] : 1;
    }
  };

  const fetchEmailTemplate = async (url = "") => {
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    const page = extractPage(url);

    try {
      const token = localStorage.getItem("userToken");

      if (!token) {
        throw new Error("Missing auth token");
      }

      const response = await fetch(`${api_url}/email-templates?page=${page}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const rows = Array.isArray(result?.data?.data) ? result.data.data : [];
      const paginationLinks = Array.isArray(result?.data?.links)
        ? result.data.links
        : [];

      setAllData(rows);
      setUsers(rows);
      setLinks(paginationLinks);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching data:", error);
        showToast(t("somethingwentwrongpleaserefresh"), "error");
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;

    setLoading(true);

    try {
      const token = localStorage.getItem("userToken");

      if (!token) {
        throw new Error("Missing auth token");
      }

      const response = await fetch(`${api_url}/email-templates/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        showToast(t("emailtemplatedeletedsuccessfully"), "success");
        setShowModal(false);
        setSelectedEmailId(null);
        fetchEmailTemplate("");
      } else {
        setShowModal(false);
        showToast(t("failedtodeleteemailtemplate"), "error");
      }
    } catch (error) {
      console.error("Delete error:", error);
      setShowModal(false);
      showToast(t("somethingwentwrongpleaserefresh"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchVal.trim() !== "") {
      const query = searchVal.toLowerCase();

      const filteredData = allData.filter((item) => {
        const title = item?.title?.toLowerCase?.() || "";
        const createdAt = item?.created_at?.toLowerCase?.() || "";
        const subject = item?.subject?.toLowerCase?.() || "";

        return (
          title.includes(query) ||
          createdAt.includes(query) ||
          subject.includes(query)
        );
      });

      setUsers(filteredData);
    } else {
      setUsers(allData);
    }
  }, [searchVal, allData]);

  useEffect(() => {
    fetchEmailTemplate("");
    localStorage.removeItem("hasFetched");

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: () => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <i className="bi bi-card-text"></i>
            {t("Title")}
          </div>
        ),
        colName: t("Title"),
      },
      {
        accessorKey: "subject",
        header: () => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <i className="bi bi-journal-bookmark"></i>
            {t("Subject")}
          </div>
        ),
        colName: t("Subject"),
      },
      {
        accessorKey: "created_at",
        header: () => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img src="/images/fi_clock.png" alt="Created At" />
            {t("CreatedAt")}
          </div>
        ),
        colName: t("CreatedAt"),
        cell: ({ row }) => {
          const rawDate = row.original.created_at;
          const parsedDate = rawDate ? new Date(rawDate) : null;

          if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
            return "-";
          }

          return parsedDate.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          });
        },
      },
      {
        accessorKey: "id",
        header: () => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img src="/images/fi_alert-octagon.png" alt="Source" />
            {t("Action")}
          </div>
        ),
        cell: ({ getValue }) => {
          const id = getValue();

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                className="iconDel"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedEmailId(id);
                  setShowModal(true);
                }}
              >
                <img src="/images/fi_trash-2.png" alt="Delete" />
                {t("Delete")}
              </div>
            </div>
          );
        },
        colName: t("Action"),
      },
    ],
    [t]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleLessonRowClick = (id) => {
    navigate(`/EmailTemplatesDetail/${id}/EmailTemplatesDetail`);
  };

  return (
    <div className="p-4 mb-4">
      <div className="pageTitle mb-4">{t("AllEmailTemplates")}</div>

      <div className="primaryBlueBox p-5 mb-4">
        <div className="row">
          <div className="col-md-4">
            <label className="form-label">{t("SearchEmailTemplate")}</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder={t("Searchbytitle")}
                aria-label="Search by name"
                value={searchVal}
                onChange={(e) => setSearchVal(e.target.value)}
              />
              <button
                className="btn btn-outline-secondary btnSearchIcon"
                type="button"
              >
                <i className="bi bi-search"></i>
              </button>
            </div>
          </div>
          <div className="col-md-1">
            <div className="vrLine"></div>
          </div>
          <div className="col-md-7">
            <NavLink className="btnWhiteGradient mt-4" to="/addemailtemplate">
              <i className="bi bi-envelope me-2 mt-3 h6"></i>{" "}
              {t("AddEmailTemplate")}
            </NavLink>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center my-5">
          <div
            className="spinner-border"
            role="status"
            style={{
              borderTopColor: "#6897CB",
              borderBottomColor: "#6897CB",
              borderLeftColor: "#6897CB",
              marginTop: "50px",
            }}
          ></div>
        </div>
      ) : (
        <div className="bg-light px-3 py-3 lessonsTableWrapper resTable">
          {isMobile ? (
            table.getRowModel().rows.map((row) => (
              <div
                key={row.id}
                className="stacked-row"
                onClick={() => handleLessonRowClick(row.original.id)}
              >
                {row.getVisibleCells().map((cell) => (
                  <div key={cell.id} className="stacked-cell">
                    <strong className="me-1">
                      {cell.column.columnDef.colName}:
                    </strong>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                ))}
              </div>
            ))
          ) : (
            <table className="table table-bordered table-hover">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="rowAnchor"
                        onClick={
                          cell.column.id !== "id"
                            ? () => handleLessonRowClick(row.original.id)
                            : undefined
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="navigationWrapper">
        <ul>
          {links.map((link, index) => (
            <li
              key={index}
              className={`${link.active ? " active " : ""}${
                link.url === null ? " disabled " : ""
              }`}
              onClick={
                link.url !== null
                  ? () => fetchEmailTemplate(link.url)
                  : undefined
              }
            >
              {sanitizeLabel(link.label)}
            </li>
          ))}
        </ul>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("ConfirmDelete")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("Areyousureyouwanttodeletethisemailtemplate?")}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            {t("Cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={() => handleDelete(selectedEmailId)}
            disabled={loading}
          >
            {loading ? t("Deleting...") : t("Delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmailTemplates;